import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Header as EnrollmentHeaderNav } from 'enrollment/components/Header';
import {
  ContentWrapperStyled,
  PageLayoutWrapperStyled,
  PublicContentWrapperStyled,
  EnrollmentContentWrapperStyled,
  HomeContentWrapperStyled,
  OrbitHeaderWrapperStyled,
  DesktopChatBotStyling,
} from './PageLayoutStyled';
import { ROUTES } from 'theSource/app/routes';
import { selectIsAuthenticated } from 'theSource/features/Login';
import {
  useCompanySettingsQuery,
  useConfigurationQuery,
} from 'theSource/queries';
import {
  useEnrollmentFamilyQuery,
  useEnrollmentProfileQuery,
} from 'enrollment/queries';
import { setSubscriber } from 'enrollment/features/Profile/services';
import { setDependents } from 'enrollment/features/Family/services';
import { Loader } from '../Loader';
import { selectLogoutButton } from 'features/HomePage/services';
import { MainHeader } from 'features/HomePage/components/MainHeader';
import { Header } from '../Header';
import { Panel } from '../Panel';
import { BottomNavigation } from '../BottomNavigation';
import { selectIsAdmin } from '../../features';
import { OliveBanner } from 'enrollment/features/OliveAdvisor/OliveBanner';
import { ChatBot } from 'components/ChatBot';
import { useSavviOverRideChosenBenefits } from 'enrollment/utils/bundledBenefitsUtils';

interface PageLayoutProps {
  navigationVisible?: boolean;
  children?: JSX.Element | JSX.Element[];
}

export const PageLayout = ({
  navigationVisible,
  children,
}: PageLayoutProps) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAdmin = useSelector(selectIsAdmin);
  const location = useLocation();
  const dispatch = useDispatch();
  const { configuration } = useConfigurationQuery();
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });
  const lumosEnabled = companySettings?.data.lumos_feature_toggle || false;
  const homeEnabled =
    companySettings?.data.lumos_home_page_feature_toggle || false;

  const { subscriber, isLoadingSubscriber } = useEnrollmentProfileQuery({
    lumosEnabled: Boolean(lumosEnabled || homeEnabled),
  });
  const { dependentsData, isLoadingDependents } = useEnrollmentFamilyQuery({
    lumosEnabled: Boolean(lumosEnabled || homeEnabled),
  });

  const isLoading = isLoadingSubscriber || isLoadingDependents;

  const logoutClicked = useSelector(selectLogoutButton);

  const showChatbot =
    companySettings?.data.chat_bot && !isAdmin && !logoutClicked;
  const savviOverRide = useSavviOverRideChosenBenefits();

  const fullWidthFormatting =
    location.pathname.split('/')[2] === 'benefits-decision-support' &&
    savviOverRide;

  useEffect(() => {
    document.body.focus();
  }, [location]);

  useEffect(() => {
    dispatch(setSubscriber(subscriber?.data));
    dispatch(setDependents(dependentsData?.data?.dependents));
  }, [subscriber, dependentsData, dispatch]);

  if (!isAuthenticated) {
    return (
      <PageLayoutWrapperStyled data-testid="page-layout">
        <PublicContentWrapperStyled>{children}</PublicContentWrapperStyled>
      </PageLayoutWrapperStyled>
    );
  }
  const isMobile = window.innerWidth < 744;
  const isTablet = window.innerWidth < 1200 && window.innerWidth > 743;
  const isTermsOfUsePage =
    location.pathname === ROUTES.public.termsAndConditions.path;

  if (lumosEnabled && location.pathname.split('/')[1] === 'enrollment') {
    return (
      <PageLayoutWrapperStyled data-testid="page-layout-enrollment">
        <EnrollmentHeaderNav
          chatBot={showChatbot && window.innerWidth < 1200}
        />
        {isMobile && <OliveBanner />}

        <EnrollmentContentWrapperStyled
          className="container"
          fullWidth={fullWidthFormatting}
        >
          {isTablet && <OliveBanner />}
          {children}
        </EnrollmentContentWrapperStyled>
        {showChatbot && (
          <DesktopChatBotStyling>
            <ChatBot
              renderDefault={true}
              useDesktopIcon={true}
              enrollment={true}
            />
          </DesktopChatBotStyling>
        )}
      </PageLayoutWrapperStyled>
    );
    // home page specific layout due to hero banner
  }
  if (homeEnabled || lumosEnabled) {
    if (location.pathname.split('/')[1] === '') {
      return (
        <PageLayoutWrapperStyled data-testid="page-layout-home" className="w-">
          <MainHeader chatBot={showChatbot && window.innerWidth < 1200} />
          <HomeContentWrapperStyled>{children}</HomeContentWrapperStyled>
        </PageLayoutWrapperStyled>
      );
    } else {
      return (
        <PageLayoutWrapperStyled data-testid="page-layout-orbit">
          <MainHeader
            chatBot={showChatbot && window.innerWidth < 1200}
            hideDrawer={isTermsOfUsePage}
          />
          <OrbitHeaderWrapperStyled>
            {isLoading ? <Loader /> : children}
          </OrbitHeaderWrapperStyled>
          {showChatbot && (
            <DesktopChatBotStyling>
              <ChatBot renderDefault={true} useDesktopIcon={true} />
            </DesktopChatBotStyling>
          )}
        </PageLayoutWrapperStyled>
      );
    }
  }

  return (
    <PageLayoutWrapperStyled data-testid="page-layout-source">
      <Header
        hideDrawer={!navigationVisible}
        chatBot={showChatbot && window.innerWidth < 1200}
      />
      <Panel hideDrawer={!navigationVisible} />
      <ContentWrapperStyled hideDrawer={!navigationVisible} id="mainContent">
        {isLoading ? <Loader /> : children}
      </ContentWrapperStyled>
      {showChatbot && (
        <DesktopChatBotStyling>
          <ChatBot renderDefault={true} useDesktopIcon={true} />
        </DesktopChatBotStyling>
      )}
      <BottomNavigation hideNavigation={!navigationVisible} />
    </PageLayoutWrapperStyled>
  );
};
